/* global PopStateEvent */
import { div, p, button, span, form, label, fnstate } from '@srfnstack/fntags'
import { showNotification, refreshCredits } from '../state.js'

const loading = fnstate(false)
const processing = fnstate(false)
const clientSecret = fnstate('')
const stripeLoaded = fnstate(false)

let stripe, elements, cardElement

const initStripe = async (packageId, planId) => {
  loading(true)
  try {
    // 1. Get client secret from backend
    const res = await fetch('/api/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ packageId, planId })
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to initialize checkout')

    clientSecret(data.clientSecret)

    // 2. Initialize Stripe
    // We'll need to fetch the publishable key from the backend eventually
    // For now we might need to hardcode a placeholder or add a config endpoint
    const configRes = await fetch('/api/system/config')
    const configData = await configRes.json()

    stripe = window.Stripe(configData.stripePublishableKey)
    elements = stripe.elements()

    cardElement = elements.create('card', {
      style: {
        base: {
          color: '#ffffff',
          fontFamily: 'inherit',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': { color: '#aab7c4' }
        },
        invalid: { color: '#fa755a', iconColor: '#fa755a' }
      }
    })

    cardElement.mount('#card-element')
    stripeLoaded(true)
  } catch (err) {
    console.error('Checkout error:', err)
    showNotification(err.message, 'error')
  } finally {
    loading(false)
  }
}

const handleSubmit = async (e) => {
  e.preventDefault()
  if (processing()) return
  processing(true)

  try {
    const result = await stripe.confirmCardPayment(clientSecret(), {
      payment_method: {
        card: cardElement,
        billing_details: { name: 'Tenant User' }
      }
    })

    if (result.error) {
      showNotification(result.error.message, 'error')
    } else {
      if (result.paymentIntent.status === 'succeeded') {
        showNotification('Payment successful!', 'success')
        await refreshCredits()
        window.history.pushState({}, '', '/finance')
        window.dispatchEvent(new PopStateEvent('popstate'))
      }
    }
  } catch (err) {
    showNotification('Payment failed', 'error')
  } finally {
    processing(false)
  }
}

export const Checkout = () => {
  const params = new URLSearchParams(window.location.search)
  const packageId = params.get('packageId')
  const planId = params.get('planId')

  setTimeout(() => initStripe(packageId, planId), 0)

  return div({ class: 'p-8 max-w-lg mx-auto' },
    div({ class: 'card bg-base-200 shadow-xl border border-base-300' },
      div({ class: 'card-body' },
        h2({ class: 'card-title text-2xl mb-4' }, 'Complete Purchase'),
        p({ class: 'mb-8 text-base-content/70' }, 'Secure checkout powered by Stripe. Your credits will be added immediately after payment.'),

        form({ onsubmit: handleSubmit },
          div({ class: 'form-control w-full mb-6' },
            label({ class: 'label' }, span({ class: 'label-text' }, 'Card Details')),
            div({ id: 'card-element', class: 'p-4 bg-base-100 rounded-lg border border-base-300' })
          ),

          div({ class: 'card-actions flex flex-col gap-4' },
            button({
              class: 'btn btn-primary btn-block',
              type: 'submit',
              disabled: processing.bindAttr(p => p || !stripeLoaded())
            }, processing.bindAs(p => p ? span({ class: 'loading loading-spinner' }) : 'Pay Now')),
            button({
              class: 'btn btn-ghost btn-block',
              type: 'button',
              onclick: () => window.history.back()
            }, 'Cancel')
          )
        )
      )
    ),
    loading.bindAs(l => l ? div({ class: 'fixed inset-0 bg-base-300/50 flex items-center justify-center z-50' }, span({ class: 'loading loading-spinner loading-lg text-primary' })) : div({ class: 'hidden' }))
  )
}

const h2 = (props, ...children) => div({ ...props, class: `font-bold ${props.class || ''}` }, ...children)
